import { debounce } from "lodash-es";

import { onViewLoad } from "jose_modern/on_view_load";
import { Spinner } from "jose_modern/spinner";
import { getCookie, setCookie } from "jose_modern/utils";
import { appendScript } from "../shared/requests";
import { unitWeights } from "../shared/constants.js.erb";
import { IngredientFiltersMixin } from "../shared/ingredient_filters_mixin";

class IngredientCostsForUser {
  constructor(container) {
    this.maybeNavigate = debounce(this.navigate, 300, { maxWait: 1000 });

    this.container = container;
    history.replaceState(window.location.href, "", window.location.href);

    this.parseUrl(window.location.href);
    this.maybeShowAdvanced();
    this.bind();
  }

  parseUrl(url) {
    const parsed = new URL(url);
    this.q = parsed.searchParams.get("q") || "";
    this.page = parsed.searchParams.get("page") || 1;
    this.archived = this.parseBool(parsed.searchParams.get("archived"), false);
    this.parseTagsFromUrl(parsed);
    this.parseIngredientTypesFromUrl(parsed);
  }

  parseBool(value, defaultValue) {
    switch (value) {
      case "true":
      case "1":
        return true;
      case "false":
      case "0":
        return false;
      default:
        return defaultValue;
    }
  }

  bind() {
    this.bindSearch();
    this.bindTags();
    this.bindIngredientTypeFilter();
    this.bindResetFilters();

    document.addEventListener("partial:replace", (e) => {
      if (e.partial === "ingredients/ingredient_costs_table") {
        this.bindTags();
      }
    });

    window.addEventListener("popstate", async (e) => {
      this.parseUrl(e.state);

      this.container.querySelector(".ingredient-search").value = this.q;
      this.setSearchFilterTags(this.tags);
      this.container.querySelector("#ingredient-cost-archived-filter").checked = this.archived;
      this.syncIngredientTypeFilter();
      this.maybeShowAdvanced();

      this.navigate(true);
    });
  }

  bindSearch() {
    const search = this.container.querySelector(".ingredient-search");
    search.addEventListener("input", async (e) => {
      this.q = e.target.value;
      this.page = 1;

      this.maybeNavigate();
    });

    this.bindTagSearchFilter();

    const archive = this.container.querySelector("#ingredient-cost-archived-filter");
    archive.addEventListener("change", async (e) => {
      this.archived = e.target.checked;
      this.page = 1;

      this.navigate();
    });
  }

  showAdvanced() {
    document.querySelector("#cost-search-advanced-filters").classList.remove("is-hidden");
  }

  resetFilters() {
    this.q = "";
    this.archived = false;
    this.ingredientTypes = [];
    this.tags = [];
    this.page = 1;

    this.container.querySelector(".ingredient-search").value = "";
    this.container.querySelector("#ingredient-cost-archived-filter").checked = false;
    this.syncIngredientTypeFilter();
    this.setSearchFilterTags(this.tags);

    this.navigate();
  }

  async navigate(skipHistory = false) {
    const url = new URL(window.location.href);
    url.searchParams.set("q", this.q);
    url.searchParams.set("page", this.page);
    url.searchParams.set("archived", this.archived);

    this.appendTagsToUrl(url);
    this.appendIngredientTypesToUrl(url);

    if (!skipHistory) {
      history.pushState(url.href, "", url.href);
    }

    this.updateResetFiltersUI();

    const resultsContainer = document.querySelector("#ingredient-costs-table-section");
    Spinner.start(resultsContainer);

    const response = await fetch(url, {
      headers: {
        Accept: "application/javascript",
      },
    });
    const data = await response.text();

    appendScript(data);
  }
}

Object.assign(IngredientCostsForUser.prototype, IngredientFiltersMixin);

function updateCostDisplays(unit) {
  const factor = unitWeights[unit];

  document.querySelectorAll(".cost-display").forEach((el) => {
    const costPerGram = el.dataset.costPerGram;
    if (!costPerGram) return; // non-food, skip

    const symbol = el.dataset.currencySymbol;
    const amount = (parseFloat(costPerGram) * factor).toFixed(2);
    el.textContent = `${symbol}${amount}/${unit}`;
  });
}

const COST_UNIT_COOKIE = "recipal_cost_unit";
let selectedCostUnit = getCookie(COST_UNIT_COOKIE) || "lb";

function bindCostUnitToggle() {
  const toggle = document.querySelector("#cost-unit-toggle");
  if (!toggle) return;

  toggle.value = selectedCostUnit;
  if (selectedCostUnit !== "lb") {
    updateCostDisplays(selectedCostUnit);
  }

  toggle.addEventListener("change", (e) => {
    selectedCostUnit = e.target.value;
    setCookie(COST_UNIT_COOKIE, selectedCostUnit);
    updateCostDisplays(selectedCostUnit);
  });
}

onViewLoad("users#costs", () => {
  new IngredientCostsForUser(document.querySelector("html"));
  bindCostUnitToggle();

  document.addEventListener("partial:replace", (e) => {
    if (e.partial === "ingredients/ingredient_costs_table") {
      bindCostUnitToggle();
    }
    if (e.partial === "ingredients/ingredient_cost" && selectedCostUnit !== "lb") {
      updateCostDisplays(selectedCostUnit);
    }
  });

  document.querySelectorAll("[data-toggle-button]").forEach((el) => {
    el.addEventListener("click", () => {
      const container = el.closest("[data-toggle-container]");

      container.querySelectorAll("[data-toggle-target]").forEach((target) => {
        const { toggleClass } = target.dataset;

        target.classList.toggle(toggleClass);
      });
    });
  });
});
